// G:\qkard\frontend\src\pages\WorkOrdersTab.jsx
// Phase 1G — Work Orders tab for furniture/tailor/tiles verticals
//
// Owner creates work orders for customers who've selected designs.
// Status flow: confirmed → in-progress → ready → delivered

import { useState, useEffect } from "react";

const API = "https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod";
const inr = (n) => "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const STATUS_CONFIG = {
  confirmed:   { label: "Confirmed",   color: "#6366f1", bg: "#f5f3ff", next: "in-progress", nextLabel: "Start Work" },
  "in-progress": { label: "In Progress", color: "#d97706", bg: "#fffbeb", next: "ready",       nextLabel: "Mark Ready" },
  ready:       { label: "Ready",       color: "#16a34a", bg: "#f0fdf4", next: "delivered",    nextLabel: "Mark Delivered" },
  delivered:   { label: "Delivered",   color: "#888",    bg: "#f5f5f5", next: null,            nextLabel: null },
};

function timeAgo(iso) {
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

const emptyForm = () => ({
  customerName: "", customerPhone: "",
  items: [{ name: "", price: "" }],
  advanceAmount: "", deliveryDate: "", notes: "",
});

export default function WorkOrdersTab({ business, items: catalogueItems }) {
  const [workOrders, setWorkOrders]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showForm, setShowForm]       = useState(false);
  const [form, setForm]               = useState(emptyForm());
  const [saving, setSaving]           = useState(false);
  const [expanded, setExpanded]       = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [updatingId, setUpdatingId]   = useState(null);

  useEffect(() => {
    loadWorkOrders();
  }, [business]);

  async function loadWorkOrders() {
    if (!business?.businessId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/workorders?businessId=${business.businessId}`);
      const data = await res.json();
      setWorkOrders(data.workOrders || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  // ── Form item helpers ────────────────────────────────────────────────────
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { name: "", price: "" }] }));
  const updateItem = (i, key, val) => setForm(f => {
    const items = [...f.items]; items[i] = { ...items[i], [key]: val };
    return { ...f, items };
  });
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const addFromCatalogue = (item) => {
    const entry = {
      name: item.name,
      price: item.variants?.length
        ? Math.min(...item.variants.map(v => v.price))
        : item.price,
      itemId: item.itemId,
      category: item.category,
    };
    setForm(f => ({ ...f, items: [...f.items.filter(i => i.name), entry] }));
  };

  const totalValue = form.items.reduce((t, i) => t + (Number(i.price) || 0), 0);
  const balance    = totalValue - (Number(form.advanceAmount) || 0);

  // ── Save work order ──────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.customerName.trim()) { alert("Customer name required"); return; }
    const validItems = form.items.filter(i => i.name.trim() && Number(i.price) > 0);
    if (validItems.length === 0) { alert("Add at least one item with price"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/workorders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          businessId:    business.businessId,
          customerName:  form.customerName.trim(),
          customerPhone: form.customerPhone.trim() || null,
          items:         validItems.map(i => ({ ...i, price: Number(i.price) })),
          advanceAmount: Number(form.advanceAmount) || 0,
          deliveryDate:  form.deliveryDate || null,
          notes:         form.notes.trim(),
        }),
      });
      const data = await res.json();
      if (data.workOrder) {
        setWorkOrders(prev => [data.workOrder, ...prev]);
        setShowForm(false);
        setForm(emptyForm());
        setExpanded(data.workOrder.workOrderId);
      }
    } catch (e) { alert("Save failed: " + e.message); }
    finally { setSaving(false); }
  };

  // ── Update status ────────────────────────────────────────────────────────
  const updateStatus = async (wo, newStatus) => {
    setUpdatingId(wo.workOrderId);
    try {
      await fetch(`${API}/workorders/${wo.workOrderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      setWorkOrders(prev => prev.map(w =>
        w.workOrderId === wo.workOrderId ? { ...w, status: newStatus } : w
      ));
    } catch (e) { alert("Update failed"); }
    finally { setUpdatingId(null); }
  };

  const filtered = filterStatus === "all"
    ? workOrders
    : workOrders.filter(w => w.status === filterStatus);

  const counts = workOrders.reduce((acc, w) => {
    acc[w.status] = (acc[w.status] || 0) + 1; return acc;
  }, {});

  return (
    <div style={s.wrap}>
      {/* Header */}
      <div style={s.headerRow}>
        <div style={s.title}>Work Orders ({workOrders.length})</div>
        <button style={s.addBtn} onClick={() => { setForm(emptyForm()); setShowForm(true); }}>
          + New Order
        </button>
      </div>

      {/* Status filter pills */}
      <div style={s.pillsRow}>
        {[
          { id: "all",         label: `All (${workOrders.length})` },
          { id: "confirmed",   label: `Confirmed (${counts.confirmed || 0})` },
          { id: "in-progress", label: `In Progress (${counts["in-progress"] || 0})` },
          { id: "ready",       label: `Ready (${counts.ready || 0})` },
          { id: "delivered",   label: `Delivered (${counts.delivered || 0})` },
        ].map(f => (
          <button key={f.id}
            style={{ ...s.pill, ...(filterStatus === f.id ? s.pillActive : {}) }}
            onClick={() => setFilterStatus(f.id)}>{f.label}</button>
        ))}
      </div>

      {/* List */}
      <div style={s.list}>
        {loading && <div style={s.empty}>Loading...</div>}
        {!loading && filtered.length === 0 && (
          <div style={s.empty}>
            {filterStatus === "all"
              ? "No work orders yet. Tap + New Order to create one."
              : `No ${filterStatus} orders.`}
          </div>
        )}

        {filtered.map(wo => {
          const cfg = STATUS_CONFIG[wo.status] || STATUS_CONFIG.confirmed;
          const isExpanded = expanded === wo.workOrderId;
          return (
            <div key={wo.workOrderId} style={{ ...s.card, borderLeft: `4px solid ${cfg.color}` }}>
              {/* Card header */}
              <div style={s.cardHeader} onClick={() => setExpanded(isExpanded ? null : wo.workOrderId)}>
                <div style={s.cardLeft}>
                  <div style={s.woRef}>{wo.referenceCode}</div>
                  <div style={s.customerName}>{wo.customerName}</div>
                  <div style={s.cardMeta}>
                    <span style={{ ...s.statusBadge, background: cfg.bg, color: cfg.color }}>
                      {cfg.label}
                    </span>
                    {wo.deliveryDate && (
                      <span style={s.deliveryDate}>
                        📅 {new Date(wo.deliveryDate).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                      </span>
                    )}
                    <span style={s.timeAgo}>{timeAgo(wo.createdAt)}</span>
                  </div>
                </div>
                <div style={s.cardRight}>
                  <div style={s.woTotal}>{inr(wo.totalValue)}</div>
                  {wo.balanceAmount > 0 && (
                    <div style={s.balance}>Bal: {inr(wo.balanceAmount)}</div>
                  )}
                  <div style={s.expandIcon}>{isExpanded ? "▲" : "▼"}</div>
                </div>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div style={s.detail}>
                  {/* Items */}
                  <div style={s.detailSection}>
                    {(wo.items || []).map((item, i) => (
                      <div key={i} style={s.detailItem}>
                        <span style={s.detailItemName}>{item.name}</span>
                        <span style={s.detailItemPrice}>{inr(item.price)}</span>
                      </div>
                    ))}
                    <div style={s.detailTotal}>
                      <span>Total</span><span>{inr(wo.totalValue)}</span>
                    </div>
                    <div style={{ ...s.detailTotal, color: "#888", fontWeight: 500, fontSize: 13 }}>
                      <span>Advance paid</span><span>{inr(wo.advanceAmount)}</span>
                    </div>
                    {wo.balanceAmount > 0 && (
                      <div style={{ ...s.detailTotal, color: "#e03535" }}>
                        <span>Balance due</span><span>{inr(wo.balanceAmount)}</span>
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  {wo.notes && (
                    <div style={s.notes}>📝 {wo.notes}</div>
                  )}

                  {/* Customer phone */}
                  {wo.customerPhone && (
                    <a href={`tel:${wo.customerPhone}`} style={s.phoneLink}>
                      📱 {wo.customerPhone}
                    </a>
                  )}

                  {/* Action buttons */}
                  <div style={s.actions}>
                    {cfg.next && (
                      <button
                        style={{ ...s.actionBtn, background: cfg.color }}
                        disabled={updatingId === wo.workOrderId}
                        onClick={() => updateStatus(wo, cfg.next)}
                      >
                        {updatingId === wo.workOrderId ? "..." : cfg.nextLabel}
                      </button>
                    )}
                    {wo.customerPhone && (
                      <button
                        style={s.whatsappBtn}
                        onClick={() => {
                          const items = (wo.items || []).map(i => `• ${i.name} — ${inr(i.price)}`).join("\n");
                          const text = `Hi ${wo.customerName}! Your work order *${wo.referenceCode}* update:\n\nItems:\n${items}\n\nTotal: ${inr(wo.totalValue)}\nAdvance: ${inr(wo.advanceAmount)}\nBalance: ${inr(wo.balanceAmount)}\nStatus: ${cfg.label}${wo.deliveryDate ? `\nDelivery: ${wo.deliveryDate}` : ""}`;
                          window.open(`https://wa.me/${wo.customerPhone.replace(/\D/g,"")}?text=${encodeURIComponent(text)}`, "_blank");
                        }}
                      >
                        📲 WhatsApp
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── New Work Order Form ──────────────────────────────────────────── */}
      {showForm && (
        <div style={s.overlay} onClick={() => !saving && setShowForm(false)}>
          <div style={s.drawer} onClick={e => e.stopPropagation()}>
            <div style={s.drawerHeader}>
              <div style={s.drawerTitle}>New Work Order</div>
              <button style={s.closeBtn} onClick={() => !saving && setShowForm(false)}>✕</button>
            </div>
            <div style={s.drawerBody}>

              {/* Customer */}
              <div style={s.section}>
                <div style={s.label}>Customer Name *</div>
                <input style={s.input} placeholder="e.g. Ramesh Gupta"
                  value={form.customerName}
                  onChange={e => setForm(f => ({ ...f, customerName: e.target.value }))} />
              </div>
              <div style={s.section}>
                <div style={s.label}>Customer Phone</div>
                <input style={s.input} placeholder="+91XXXXXXXXXX" type="tel"
                  value={form.customerPhone}
                  onChange={e => setForm(f => ({ ...f, customerPhone: e.target.value }))} />
              </div>

              {/* Quick add from catalogue */}
              {catalogueItems?.length > 0 && (
                <div style={s.section}>
                  <div style={s.label}>Quick Add from Catalogue</div>
                  <div style={s.quickAdd}>
                    {catalogueItems.slice(0, 8).map(item => (
                      <button key={item.itemId} style={s.quickAddChip}
                        onClick={() => addFromCatalogue(item)}>
                        + {item.name.length > 20 ? item.name.slice(0, 20) + "…" : item.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Items */}
              <div style={s.section}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={s.label}>Items *</div>
                  <button style={s.addVariantBtn} onClick={addItem}>+ Add Item</button>
                </div>
                {form.items.map((item, i) => (
                  <div key={i} style={s.variantRow}>
                    <input style={{ ...s.input, flex: 2 }} placeholder="Item name"
                      value={item.name} onChange={e => updateItem(i, "name", e.target.value)} />
                    <input style={{ ...s.input, flex: 1 }} placeholder="₹" type="number"
                      value={item.price} onChange={e => updateItem(i, "price", e.target.value)} />
                    {form.items.length > 1 && (
                      <button style={s.removeBtn} onClick={() => removeItem(i)}>✕</button>
                    )}
                  </div>
                ))}
                {totalValue > 0 && (
                  <div style={s.runningTotal}>Total: {inr(totalValue)}</div>
                )}
              </div>

              {/* Advance */}
              <div style={s.section}>
                <div style={s.label}>Advance Received (₹)</div>
                <input style={s.input} placeholder="e.g. 5000" type="number"
                  value={form.advanceAmount}
                  onChange={e => setForm(f => ({ ...f, advanceAmount: e.target.value }))} />
                {Number(form.advanceAmount) > 0 && totalValue > 0 && (
                  <div style={s.balanceHint}>
                    Balance due: {inr(Math.max(0, totalValue - Number(form.advanceAmount)))}
                  </div>
                )}
              </div>

              {/* Delivery date */}
              <div style={s.section}>
                <div style={s.label}>Delivery Date</div>
                <input style={s.input} type="date"
                  value={form.deliveryDate}
                  onChange={e => setForm(f => ({ ...f, deliveryDate: e.target.value }))} />
              </div>

              {/* Notes */}
              <div style={s.section}>
                <div style={s.label}>Notes / Measurements</div>
                <textarea style={{ ...s.input, minHeight: 72, resize: "vertical" }}
                  placeholder="Any special instructions, measurements, colour preference..."
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>

              <button style={{ ...s.saveBtn, ...(saving ? { background: "#999" } : {}) }}
                onClick={handleSave} disabled={saving}>
                {saving ? "Creating..." : "Create Work Order"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  wrap: { padding: "0 0 80px" },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 16px 12px" },
  title: { fontSize: 18, fontWeight: 700, color: "#111" },
  addBtn: { background: "#111", color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  pillsRow: { display: "flex", gap: 8, overflowX: "auto", padding: "0 16px 12px", scrollbarWidth: "none" },
  pill: { flexShrink: 0, padding: "5px 12px", borderRadius: 20, border: "1.5px solid #ddd", background: "transparent", color: "#666", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" },
  pillActive: { background: "#111", borderColor: "#111", color: "#fff" },
  list: { padding: "0 16px" },
  card: { background: "#fff", borderRadius: 12, marginBottom: 10, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", border: "1px solid #f0f0f0" },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "14px 16px", cursor: "pointer" },
  cardLeft: { flex: 1 },
  woRef: { fontSize: 13, fontWeight: 800, fontFamily: "monospace", color: "#111", letterSpacing: "0.5px", marginBottom: 2 },
  customerName: { fontSize: 15, fontWeight: 600, color: "#111", marginBottom: 6 },
  cardMeta: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  statusBadge: { fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, textTransform: "uppercase", letterSpacing: "0.3px" },
  deliveryDate: { fontSize: 11, color: "#888" },
  timeAgo: { fontSize: 11, color: "#bbb" },
  cardRight: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 },
  woTotal: { fontSize: 16, fontWeight: 700, color: "#111" },
  balance: { fontSize: 11, color: "#e03535", fontWeight: 600 },
  expandIcon: { fontSize: 10, color: "#ccc", marginTop: 4 },
  detail: { borderTop: "1px solid #f5f5f5", padding: "12px 16px 16px" },
  detailSection: { marginBottom: 12 },
  detailItem: { display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 13, borderBottom: "1px solid #f9f9f9" },
  detailItemName: { color: "#333" },
  detailItemPrice: { fontWeight: 600, color: "#111" },
  detailTotal: { display: "flex", justifyContent: "space-between", padding: "8px 0 0", fontSize: 14, fontWeight: 700, color: "#111" },
  notes: { fontSize: 13, color: "#666", background: "#f9f9f9", borderRadius: 8, padding: "8px 12px", marginBottom: 10 },
  phoneLink: { display: "block", fontSize: 13, color: "#6366f1", fontWeight: 600, textDecoration: "none", marginBottom: 12 },
  actions: { display: "flex", gap: 8 },
  actionBtn: { flex: 1, padding: "10px", borderRadius: 8, border: "none", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  whatsappBtn: { padding: "10px 16px", borderRadius: 8, border: "none", background: "#25D366", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  empty: { textAlign: "center", padding: "48px 20px", color: "#999", fontSize: 14 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 500, display: "flex", alignItems: "flex-end" },
  drawer: { background: "#fff", width: "100%", maxWidth: 640, margin: "0 auto", borderRadius: "20px 20px 0 0", maxHeight: "92vh", display: "flex", flexDirection: "column" },
  drawerHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid #f0f0f0", flexShrink: 0 },
  drawerTitle: { fontSize: 17, fontWeight: 700, color: "#111" },
  closeBtn: { background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#999" },
  drawerBody: { overflowY: "auto", padding: "16px 20px 32px", flex: 1 },
  section: { marginBottom: 20 },
  label: { fontSize: 13, fontWeight: 600, color: "#333", marginBottom: 8 },
  input: { width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e0e0e0", fontSize: 14, color: "#111", fontFamily: "inherit", outline: "none", boxSizing: "border-box", background: "#fafafa" },
  quickAdd: { display: "flex", gap: 8, flexWrap: "wrap" },
  quickAddChip: { padding: "5px 12px", borderRadius: 16, border: "1.5px solid #ddd", background: "#fafafa", fontSize: 12, cursor: "pointer", color: "#333" },
  variantRow: { display: "flex", gap: 8, marginBottom: 8, alignItems: "center" },
  addVariantBtn: { background: "none", border: "1.5px solid #ddd", borderRadius: 6, padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#333" },
  removeBtn: { background: "none", border: "none", fontSize: 16, cursor: "pointer", color: "#ccc", padding: "0 4px" },
  runningTotal: { fontSize: 13, fontWeight: 700, color: "#b8722a", marginTop: 6, textAlign: "right" },
  balanceHint: { fontSize: 12, color: "#e03535", marginTop: 6 },
  saveBtn: { width: "100%", padding: 14, borderRadius: 10, border: "none", background: "#111", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer" },
};
